import { ColumnElement } from '@atoms/columnElement';
import { InputInnerLabel } from '@atoms/InputInnerLabel';
import { ExempleTooltip, InfoTooltip } from '@atoms/infoTooltip';
import { SwitchV2 } from '@atoms/switch';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@components/ui/card';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@components/ui/select';
import type { AtomModel } from '@models/atom.model';
import {
  type CurrencyDisplay,
  type NumberFormatOptions,
  type NumberStyle,
  PositiveIntegerSchema
} from '@stratumn/studio-shared';
import {
  CURRENCY_DISPLAY_LIST,
  CurrencyToInfoMap,
  STYLE_LIST,
  StyleToInfoMap
} from '@widgets/number/number.widget.config';
import { observer } from 'mobx-react';
import { parseWithZod } from '@/utils/parseZodSchema';

type Props = {
  settingsTitle?: string;
  options: NumberFormatOptions;
  onOptionsChange: (options: NumberFormatOptions) => void;
  atomId: AtomModel['id'];
};

export const NumberWidgetSettings = observer((props: Props) => {
  const onStyleChange = (style: NumberStyle) => {
    props.onOptionsChange({
      ...props.options,
      currency: style === 'currency' ? props.options.currency : undefined,
      currencyDisplay:
        style === 'currency' ? props.options.currencyDisplay : undefined,
      style
    });
  };

  const settingsTitle = props.settingsTitle ?? 'Number display format settings';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{settingsTitle}</CardTitle>
        <CardDescription>Additional format settings</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap gap-6">
            <Column $gap="1rem" $width="200px">
              <ColumnElement>
                <Label htmlFor={`style-${props.atomId}`}>Style</Label>
                <Select
                  onValueChange={onStyleChange}
                  value={props.options.style ?? 'decimal'}
                >
                  <SelectTrigger id={`style-${props.atomId}`}>
                    <SelectValue
                      placeholder={renderStyleValue(
                        props.options.style ?? 'decimal'
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {STYLE_LIST.map(style => (
                        <SelectItem key={style} value={style}>
                          {renderStyleValue(style)}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </ColumnElement>

              <ColumnElement>
                <SwitchV2
                  checked={props.options.useGrouping ?? true}
                  id={`use-grouping-${props.atomId}`}
                  labelText="Use grouping"
                  onCheckedChange={useGrouping =>
                    props.onOptionsChange({
                      ...props.options,
                      useGrouping
                    })
                  }
                  tooltip={
                    <InfoTooltip
                      description="Whether to use grouping separators, such as thousands separators."
                      title="Use grouping"
                    />
                  }
                />
              </ColumnElement>
            </Column>
            {props.options.style === 'currency' && (
              <>
                <Column $width="200px">
                  {props.options.style === 'currency' && (
                    <div className="grid w-full max-w-sm items-center gap-1.5">
                      <Label htmlFor={`currency-${props.atomId}`}>
                        Currency
                      </Label>
                      <Input
                        id={`currency-${props.atomId}`}
                        onChange={e =>
                          props.onOptionsChange({
                            ...props.options,
                            currency: e.target.value
                          })
                        }
                        placeholder='Ex: "USD"'
                        value={props.options.currency}
                      />
                    </div>
                  )}
                </Column>
                <Column>
                  <Label htmlFor={`currency-display-${props.atomId}`}>
                    Currency display
                  </Label>

                  <RadioGroup
                    id={`currency-display-${props.atomId}`}
                    onValueChange={currencyDisplay =>
                      props.onOptionsChange({
                        ...props.options,
                        currencyDisplay: currencyDisplay as CurrencyDisplay
                      })
                    }
                    value={
                      props.options.currencyDisplay ??
                      ('symbol' satisfies CurrencyDisplay)
                    }
                  >
                    {CURRENCY_DISPLAY_LIST.map(currencyDisplay => (
                      <div
                        className="flex items-center space-x-2"
                        key={currencyDisplay}
                      >
                        <RadioGroupItem
                          id={currencyDisplay}
                          value={currencyDisplay}
                        />
                        <Label
                          className="cursor-pointer font-normal"
                          htmlFor={currencyDisplay}
                        >
                          {CurrencyToInfoMap[currencyDisplay].name}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </Column>
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-6">
            <Column>
              <div className="flex items-center gap-2">
                <Label htmlFor={`min-fraction-${props.atomId}`}>
                  Fraction digits
                </Label>
                <InfoTooltip
                  additionalContent={
                    <>
                      <ExempleTooltip
                        exampleName="Min fraction digits 3"
                        input="12.3"
                        output="12.300"
                      />
                      <ExempleTooltip
                        exampleName="Max fraction digits 3"
                        input="12.3459"
                        output="12.346"
                      />
                    </>
                  }
                  description="Leave blank to use the default value."
                  title="Fraction digits"
                />
              </div>

              <div className="flex w-full flex-col justify-between gap-2 align-top">
                <InputInnerLabel
                  id={`min-fraction-${props.atomId}`}
                  innerLabel="Min"
                  min={0}
                  onChange={e =>
                    props.onOptionsChange({
                      ...props.options,
                      minimumFractionDigits: toNumber(e.target.value)
                    })
                  }
                  placeholder="Ex: 2"
                  step={1}
                  type="number"
                  value={props.options.minimumFractionDigits}
                />
                <InputInnerLabel
                  id={`max-fraction-${props.atomId}`}
                  innerLabel="Max"
                  min={0}
                  onChange={e =>
                    props.onOptionsChange({
                      ...props.options,
                      maximumFractionDigits: toNumber(e.target.value)
                    })
                  }
                  placeholder="Ex: 2"
                  step={1}
                  type="number"
                  value={props.options.maximumFractionDigits}
                />
              </div>
            </Column>

            <Column>
              <div className="flex items-center gap-2">
                <Label htmlFor={`min-significant-${props.atomId}`}>
                  Significant digits
                </Label>

                <InfoTooltip
                  additionalContent={
                    <>
                      <ExempleTooltip
                        exampleName="Min significant digits 3"
                        input="12"
                        output="12.0"
                      />
                      <ExempleTooltip
                        exampleName="Max significant digits 3"
                        input="123456.789"
                        output="123,000"
                      />
                    </>
                  }
                  description="Leave blank to use the default value."
                  title="Significant digits"
                />
              </div>
              <div className="flex w-full flex-col justify-between gap-2 align-top">
                <InputInnerLabel
                  id={`min-significant-${props.atomId}`}
                  innerLabel="Min"
                  min={0}
                  onChange={e =>
                    props.onOptionsChange({
                      ...props.options,
                      minimumSignificantDigits: toNumber(e.target.value)
                    })
                  }
                  placeholder="Ex: 2"
                  step={1}
                  type="number"
                  value={props.options.minimumSignificantDigits}
                />
                <InputInnerLabel
                  id={`max-significant-${props.atomId}`}
                  innerLabel="Max"
                  min={0}
                  onChange={e =>
                    props.onOptionsChange({
                      ...props.options,
                      maximumSignificantDigits: toNumber(e.target.value)
                    })
                  }
                  placeholder="Ex: 2"
                  step={1}
                  type="number"
                  value={props.options.maximumSignificantDigits}
                />
              </div>
            </Column>

            <Column>
              <div className="flex items-center gap-2">
                <Label htmlFor={`min-integer-${props.atomId}`}>
                  Integer digits
                </Label>

                <InfoTooltip
                  additionalContent=<ExempleTooltip
                    exampleName="Min integer digits 3"
                    input="12.5"
                    output="012.5"
                  />
                  description="Leave blank to use the default value."
                  title="Integer digits"
                />
              </div>

              <div className="flex w-full flex-col justify-between gap-2 align-top">
                <InputInnerLabel
                  id={`min-integer-${props.atomId}`}
                  innerLabel="Min"
                  min={0}
                  onChange={e =>
                    props.onOptionsChange({
                      ...props.options,
                      minimumIntegerDigits: toNumber(e.target.value)
                    })
                  }
                  placeholder="Ex: 2"
                  step={1}
                  type="number"
                  value={props.options.minimumIntegerDigits}
                />
              </div>
            </Column>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

const toNumber = (value: unknown): Maybe<number> => {
  if (value === '') {
    return;
  }

  return parseWithZod(PositiveIntegerSchema, Number(value), 'POSINT-1', {
    withSentry: false,
    withToast: false
  });
};

const renderStyleValue = (style: NumberStyle) => {
  const { name, icon: StyleIcon } = StyleToInfoMap[style];
  return (
    <div className="flex items-center gap-2">
      <StyleIcon size={16} />
      <p>{name}</p>
    </div>
  );
};
